// Balanced journal lines from Wodi's financial events
// (docs/plans/phase3-accounting-and-revenue-share.md §3). Pure: the API reads the facts and
// these functions say which accounts they debit and credit. Every builder returns lines
// whose debits equal their credits; amounts are integer cents, never negative in a column.

import type { PaymentMethod, RefundMethod } from '../billing/shift.ts';
import type { ServiceItemCategory } from '../catalogue.ts';
import type { RemittanceMethod } from '../insurance/remittance.ts';
import { divideRoundedHalfAwayFromZero } from '../money.ts';
import type { AccountKey, PayoutMethod } from './accounts.ts';
import {
  payoutAccountOf,
  paymentAccountOf,
  refundAccountOf,
  remittanceAccountOf,
  revenueAccountOf,
} from './accounts.ts';

export const JOURNAL_EVENT_KINDS = [
  'charge',
  'waiver',
  'discount',
  'credit_note',
  'payment',
  'refund',
  'shift_variance',
  'sha_settlement',
  'sha_write_off',
  'insurer_remittance',
  'insurer_write_off',
  'goods_receipt',
  'goods_receipt_reversal',
  'supplier_invoice',
  'stock_issue',
  'stock_adjustment',
  'opening_balance',
  'revenue_share_statement',
  'revenue_share_payout',
] as const;
export type JournalEventKind = (typeof JOURNAL_EVENT_KINDS)[number];

export const JOURNAL_EVENT_WORDS: Record<JournalEventKind, string> = {
  charge: 'Charges',
  waiver: 'Waivers',
  discount: 'Discounts',
  credit_note: 'Credit notes',
  payment: 'Payments received',
  refund: 'Refunds paid',
  shift_variance: 'Cashier shift variances',
  sha_settlement: 'SHA claim payments',
  sha_write_off: 'SHA claim write-offs',
  insurer_remittance: 'Insurer payments',
  insurer_write_off: 'Insurer write-offs',
  goods_receipt: 'Goods received',
  goods_receipt_reversal: 'Goods received reversed',
  supplier_invoice: 'Supplier invoices',
  stock_issue: 'Stock issued to patients',
  stock_adjustment: 'Stock adjustments and counts',
  opening_balance: 'Opening stock balances',
  revenue_share_statement: 'Doctor revenue share statements',
  revenue_share_payout: 'Doctor revenue share payouts',
};

export interface JournalLine {
  readonly account: AccountKey;
  /** The payer, for payer-scoped accounts; null otherwise. */
  readonly scopeId: string | null;
  /** A payer or supplier name for the accounting package; never a patient. */
  readonly counterparty: string | null;
  readonly debitCents: bigint;
  readonly creditCents: bigint;
}

export interface LineSide {
  readonly account: AccountKey;
  readonly scopeId?: string | null;
  readonly counterparty?: string | null;
}

function line(side: LineSide, debitCents: bigint, creditCents: bigint): JournalLine {
  return {
    account: side.account,
    scopeId: side.scopeId ?? null,
    counterparty: side.counterparty ?? null,
    debitCents,
    creditCents,
  };
}

/**
 * Debit one side and credit the other with the same amount. A negative amount swaps the
 * sides, so a reversal is written as positive figures in the opposite columns. Zero writes
 * nothing.
 */
export function pairLines(debit: LineSide, credit: LineSide, cents: bigint): JournalLine[] {
  if (cents === 0n) return [];
  if (cents < 0n) return pairLines(credit, debit, -cents);
  return [line(debit, cents, 0n), line(credit, 0n, cents)];
}

export function totalsOf(lines: readonly JournalLine[]): { debit: bigint; credit: bigint } {
  let debit = 0n;
  let credit = 0n;
  for (const l of lines) {
    debit += l.debitCents;
    credit += l.creditCents;
  }
  return { debit, credit };
}

export function isBalanced(lines: readonly JournalLine[]): boolean {
  const t = totalsOf(lines);
  return t.debit === t.credit;
}

const PATIENTS: LineSide = { account: 'receivable.patients' };

function payerSide(payerId: string, payerName: string): LineSide {
  return { account: 'receivable.payer', scopeId: payerId, counterparty: payerName };
}

// --- Billing ------------------------------------------------------------------------------

export interface ChargeFacts {
  readonly category: ServiceItemCategory;
  readonly totalCents: bigint;
  readonly payerCents: bigint;
  readonly patientCents: bigint;
  readonly payerId: string;
  readonly payerName: string;
}

/** A priced charge: revenue for its category, owed by the patient and the payer. */
export function chargeLines(c: ChargeFacts): JournalLine[] {
  if (c.payerCents + c.patientCents !== c.totalCents)
    throw new RangeError('A charge’s payer and patient shares must add up to its total');
  const lines: JournalLine[] = [];
  if (c.patientCents !== 0n) lines.push(line(PATIENTS, c.patientCents, 0n));
  if (c.payerCents !== 0n) lines.push(line(payerSide(c.payerId, c.payerName), c.payerCents, 0n));
  if (c.totalCents !== 0n)
    lines.push(line({ account: revenueAccountOf(c.category) }, 0n, c.totalCents));
  return lines;
}

/** An approved waiver or discount comes off what the patient owes. */
export function adjustmentLines(kind: 'waiver' | 'discount', cents: bigint): JournalLine[] {
  return pairLines(
    { account: kind === 'waiver' ? 'contra.waivers' : 'contra.discounts' },
    PATIENTS,
    cents,
  );
}

export function creditNoteLines(cents: bigint): JournalLine[] {
  return pairLines({ account: 'contra.credit_notes' }, PATIENTS, cents);
}

export function paymentLines(method: PaymentMethod, cents: bigint): JournalLine[] {
  return pairLines({ account: paymentAccountOf(method) }, PATIENTS, cents);
}

export function refundLines(method: RefundMethod, cents: bigint): JournalLine[] {
  return pairLines(PATIENTS, { account: refundAccountOf(method) }, cents);
}

/** counted − expected: more cash than expected is a gain, less is a loss. */
export function shiftVarianceLines(varianceCents: bigint): JournalLine[] {
  return pairLines({ account: 'money.cash' }, { account: 'cash.over_short' }, varianceCents);
}

// --- Payers ---------------------------------------------------------------------------------

export function shaSettlementLines(payerId: string, payerName: string, cents: bigint) {
  return pairLines({ account: 'money.bank' }, payerSide(payerId, payerName), cents);
}

export function remittanceLines(
  method: RemittanceMethod,
  payerId: string,
  payerName: string,
  cents: bigint,
): JournalLine[] {
  return pairLines({ account: remittanceAccountOf(method) }, payerSide(payerId, payerName), cents);
}

export function payerWriteOffLines(payerId: string, payerName: string, cents: bigint) {
  return pairLines({ account: 'payer.write_offs' }, payerSide(payerId, payerName), cents);
}

// --- Purchasing and stock -----------------------------------------------------------------

export function goodsReceiptLines(costCents: bigint, supplierName: string): JournalLine[] {
  return pairLines(
    { account: 'inventory' },
    { account: 'goods_received_not_invoiced', counterparty: supplierName },
    costCents,
  );
}

export function goodsReceiptReversalLines(costCents: bigint, supplierName: string) {
  return goodsReceiptLines(-costCents, supplierName);
}

export interface SupplierInvoiceFacts {
  readonly linesTotalCents: bigint;
  readonly vatCents: bigint;
  readonly totalCents: bigint;
  readonly supplierName: string;
}

/**
 * An approved supplier invoice clears goods received not invoiced for its lines, posts the
 * printed VAT, and owes the supplier the printed total. A printed total that differs from
 * lines plus VAT posts its difference to purchase rounding, so the entry still balances.
 */
export function supplierInvoiceLines(i: SupplierInvoiceFacts): JournalLine[] {
  const supplier: LineSide = { account: 'payables.suppliers', counterparty: i.supplierName };
  const lines: JournalLine[] = [];
  if (i.linesTotalCents !== 0n)
    lines.push(
      line(
        { account: 'goods_received_not_invoiced', counterparty: i.supplierName },
        i.linesTotalCents,
        0n,
      ),
    );
  if (i.vatCents !== 0n) lines.push(line({ account: 'vat.input' }, i.vatCents, 0n));
  const difference = i.totalCents - i.linesTotalCents - i.vatCents;
  if (difference > 0n) lines.push(line({ account: 'purchases.rounding' }, difference, 0n));
  if (difference < 0n) lines.push(line({ account: 'purchases.rounding' }, 0n, -difference));
  if (i.totalCents !== 0n) lines.push(line(supplier, 0n, i.totalCents));
  return lines;
}

export type StockJournalKind = 'stock_issue' | 'stock_adjustment' | 'opening_balance';

const STOCK_COUNTER_ACCOUNT: Record<StockJournalKind, AccountKey> = {
  stock_issue: 'cost_of_sales',
  stock_adjustment: 'stock.losses_gains',
  opening_balance: 'opening_balances',
};

/**
 * A stock movement valued at cost. `valueCents` has the movement's sign: stock leaving the
 * store (a dispense, a loss) is negative and debits the counter account; stock coming back
 * (a return, a find, an opening balance) is positive and debits inventory.
 */
export function stockMovementLines(kind: StockJournalKind, valueCents: bigint): JournalLine[] {
  return pairLines({ account: 'inventory' }, { account: STOCK_COUNTER_ACCOUNT[kind] }, valueCents);
}

/**
 * The value of a movement of `quantity` base units from a batch received at `packCostCents`
 * for `baseUnitsPerPack` units, rounded half away from zero once (ADR 0006). Signed like the
 * quantity.
 */
export function movementValueCents(
  quantity: number,
  packCostCents: bigint,
  baseUnitsPerPack: number,
): bigint {
  if (!Number.isSafeInteger(quantity)) throw new RangeError('quantity must be a whole number');
  if (!Number.isSafeInteger(baseUnitsPerPack) || baseUnitsPerPack <= 0)
    throw new RangeError('units per pack must be a positive whole number');
  return divideRoundedHalfAwayFromZero(BigInt(quantity) * packCostCents, BigInt(baseUnitsPerPack));
}

// --- Doctor revenue share -------------------------------------------------------------------

/** An approved statement: the share is an expense owed to the practitioner. */
export function revenueShareStatementLines(payableCents: bigint): JournalLine[] {
  return pairLines(
    { account: 'doctor_share.expense' },
    { account: 'doctor_share.payable' },
    payableCents,
  );
}

export function revenueSharePayoutLines(method: PayoutMethod, cents: bigint): JournalLine[] {
  return pairLines(
    { account: 'doctor_share.payable' },
    { account: payoutAccountOf(method) },
    cents,
  );
}
